// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The typed building model as per-element solids (frontend-r22.27): what
 * the Ground draws in 3D instead of the reconstruction mesh. The server's
 * `GET /elements` gives every element its own mesh (the solids stage's
 * `solid_*` meshes split by their element index, a box in the wall for
 * each door and window — ReUseX io/export_scene); against a server without
 * the route, `elementsFromModel` builds the same list from `GET /walls`,
 * `/openings`, `/spaces` and `/slabs` (walls as boxes without their
 * openings cut, spaces and slabs as prisms of their footprints). The mock
 * backend (`scripts/mock-ruxd/solid.ts`) serves that list too, so this file
 * imports types only (Node runs the mock without a bundler). Pure: no
 * React, no DOM — three's `ShapeUtils` is plain maths.
 */

import { ShapeUtils, Vector2 } from 'three';

import type { BuildingElement, ElementCategory, PlanXY, SolidOpening, SolidSlab, SolidSpace, SolidWall } from '../api/types';
import { joinedFootprints } from './wallJoins';

/** The typed export's categories, in its order (`io::element_categories()`). */
export const ELEMENT_CATEGORIES: readonly ElementCategory[] = ['wall', 'slab', 'roof', 'space', 'door', 'window', 'opening'];

/** A wall whose thickness is not known is drawn this thick (metres; `export_scene`'s fallback). */
const DEFAULT_WALL_THICKNESS = 0.1;
/** A door or window box stands this far proud of each wall face, so it reads over the uncut wall. */
const OPENING_PROUD = 0.01;

const COLOR_TOKEN: Record<ElementCategory, string> = {
  wall: '--mesh-surface',
  slab: '--color-border-strong',
  roof: '--type-7',
  space: '--label-2',
  door: '--label-0',
  window: '--label-1',
  opening: '--label-7',
};

/** The surface colour token a category is drawn in (walls the imported model's, `modelLayers.kindColorToken`). */
export function elementColorToken(category: ElementCategory): string {
  return COLOR_TOKEN[category] ?? '--mesh-surface';
}

/** Spaces are volumes of air: drawn see-through, so the walls around them show. */
export function isTranslucentCategory(category: ElementCategory): boolean {
  return category === 'space';
}

const LABEL: Record<ElementCategory, string> = {
  wall: 'Væg',
  slab: 'Dæk',
  roof: 'Tag',
  space: 'Rum',
  door: 'Dør',
  window: 'Vindue',
  opening: 'Åbning',
};

/** "Rum · Kontor · Stue": the element's kind, its own name and its storey's, those it has. */
export function elementsSummary(element: Pick<BuildingElement, 'category' | 'name' | 'storey_id'>, storeyName: (id: number | null) => string | null): string {
  return [LABEL[element.category] ?? element.category, element.name || null, storeyName(element.storey_id)].filter(Boolean).join(' · ');
}

/** The element's lowest and highest world z; null without vertices. */
export function elementZRange(element: Pick<BuildingElement, 'positions'>): { zMin: number; zMax: number } | null {
  const p = element.positions;
  if (p.length < 3) return null;
  let zMin = Infinity;
  let zMax = -Infinity;
  for (let i = 2; i < p.length; i += 3) {
    if (p[i] < zMin) zMin = p[i];
    if (p[i] > zMax) zMax = p[i];
  }
  return { zMin, zMax };
}

/**
 * The elements the 3D view draws: those of a shown category. The storey is
 * not filtered here — Etage sets the clipping box (`storeyClip.ts`).
 */
export function elementsDrawn(elements: readonly BuildingElement[] | null | undefined, hidden: ReadonlySet<string>): BuildingElement[] {
  return (elements ?? []).filter((e) => !hidden.has(e.category));
}

// --- the fallback: solids from the rows ---------------------------------------

/** A ring without a repeated closing point or zero-length edges. */
function cleanRing(ring: readonly PlanXY[]): PlanXY[] {
  const out: PlanXY[] = [];
  for (const p of ring) {
    const q = out[out.length - 1];
    if (!q || Math.hypot(p[0] - q[0], p[1] - q[1]) > 1e-9) out.push([p[0], p[1]]);
  }
  while (out.length > 1 && Math.hypot(out[0][0] - out[out.length - 1][0], out[0][1] - out[out.length - 1][1]) <= 1e-9) out.pop();
  return out;
}

/**
 * A closed prism: `outer` with `holes` (world x/y, open rings) swept from
 * `z0` to `z1`. Caps are ear-clipped (any simple polygon), sides two
 * triangles per edge. Null for a footprint with fewer than three corners.
 */
function prism(outer: readonly PlanXY[], holes: readonly (readonly PlanXY[])[], z0: number, z1: number): { positions: number[]; indices: number[] } | null {
  const contour = cleanRing(outer);
  if (contour.length < 3) return null;
  const rings = [contour, ...holes.map(cleanRing).filter((h) => h.length >= 3)];
  const points = rings.flat();
  const n = points.length;
  const faces = ShapeUtils.triangulateShape(
    contour.map((p) => new Vector2(p[0], p[1])),
    rings.slice(1).map((h) => h.map((p) => new Vector2(p[0], p[1]))),
  );
  if (faces.length === 0) return null;

  const positions: number[] = [];
  for (const z of [z0, z1]) for (const p of points) positions.push(p[0], p[1], z);
  const indices: number[] = [];
  for (const [a, b, c] of faces) indices.push(a, c, b, n + a, n + b, n + c);
  let offset = 0;
  for (const ring of rings) {
    const m = ring.length;
    for (let i = 0; i < m; i += 1) {
      const a = offset + i;
      const b = offset + ((i + 1) % m);
      indices.push(a, b, n + b, a, n + b, n + a);
    }
    offset += m;
  }
  return { positions, indices };
}

/** The four corners of a band `halfWidth` either side of segment a→b; null for a degenerate segment. */
function band(a: PlanXY, b: PlanXY, halfWidth: number): PlanXY[] | null {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = Math.hypot(dx, dy);
  if (!(len > 1e-6) || !(halfWidth > 0)) return null;
  const nx = (-dy / len) * halfWidth;
  const ny = (dx / len) * halfWidth;
  return [
    [a[0] + nx, a[1] + ny],
    [b[0] + nx, b[1] + ny],
    [b[0] - nx, b[1] - ny],
    [a[0] - nx, a[1] - ny],
  ];
}

export interface ElementModelSource {
  walls: readonly SolidWall[] | null | undefined;
  openings: readonly SolidOpening[] | null | undefined;
  spaces: readonly SolidSpace[] | null | undefined;
  slabs: readonly SolidSlab[] | null | undefined;
}

/**
 * Per-element solids built from the building model's rows, in the typed
 * export's category order — for a server without `GET /elements`. Rows
 * that make no solid (a wall without length, an opening with no host or
 * no world position, a footprint with fewer than three corners) are left
 * out.
 */
export function elementsFromModel(source: ElementModelSource): BuildingElement[] {
  const out: BuildingElement[] = [];
  const add = (
    category: ElementCategory,
    row: { guid: string; storey_id: number | null; storey_guid: string | null },
    solid: { positions: number[]; indices: number[] } | null,
    extra: { name?: string; host_guid?: string | null } = {},
  ) => {
    if (!solid) return;
    out.push({
      guid: row.guid,
      category,
      name: extra.name ?? '',
      storey_id: row.storey_id,
      storey_guid: row.storey_guid,
      host_guid: extra.host_guid ?? null,
      positions: solid.positions,
      indices: solid.indices,
    });
  };

  const hosts = new Map<string, SolidWall>();
  const wallHalf = (w: SolidWall) => (w.thickness && w.thickness > 0 ? w.thickness : DEFAULT_WALL_THICKNESS) / 2;
  // Joined walls meet: corners mitred, tee stems up to the through wall's face (frontend-5yu).
  const footprints = joinedFootprints((source.walls ?? []).map((w) => ({ guid: w.guid, start: w.start, end: w.end, half: wallHalf(w), group: w.storey_id })));
  for (const w of source.walls ?? []) {
    hosts.set(w.guid, w);
    const ring = footprints.get(w.guid) ?? null;
    add('wall', w, ring && ring.length === 4 ? prism(ring.map((p): PlanXY => [p[0], p[1]]), [], w.z_base, w.z_top) : null);
  }
  const slabs = source.slabs ?? [];
  for (const kind of ['floor', 'roof'] as const) {
    for (const s of slabs) {
      if (s.kind !== kind) continue;
      add(kind === 'roof' ? 'roof' : 'slab', s, prism(s.polygon.outer, s.polygon.holes, s.z_base, s.z_top));
    }
  }
  for (const s of source.spaces ?? []) add('space', s, prism(s.polygon.outer, s.polygon.holes, s.z_floor, s.z_ceiling), { name: s.name });
  for (const kind of ['door', 'window', 'opening'] as const) {
    for (const o of source.openings ?? []) {
      if (o.kind !== kind || !o.start || !o.end) continue;
      const host = hosts.get(o.wall_guid);
      if (!host) continue;
      const half = (host.thickness && host.thickness > 0 ? host.thickness : DEFAULT_WALL_THICKNESS) / 2 + OPENING_PROUD;
      const ring = band(o.start, o.end, half);
      const storey = o.storey_id ?? host.storey_id;
      add(
        kind,
        { guid: o.guid, storey_id: storey, storey_guid: o.storey_guid ?? host.storey_guid },
        ring ? prism(ring, [], o.z_sill, o.z_head) : null,
        { host_guid: o.wall_guid },
      );
    }
  }
  return out;
}
